import React from 'react'
import PropTypes from 'prop-types'
import 'react-virtualized/styles.css'
import classNames from 'classnames'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { compose } from 'recompose'
import { List, Map } from 'immutable'
import { Table, Column } from 'react-virtualized'
import { withRouter } from 'react-router-dom'

import withCurrentSite from 'modules/sites/containers/with-current-site'
import { actions, selectors } from 'modules/links'
import {queueStatuses} from 'modules/queues/enum'
import { isSitemapParsed } from 'modules/queues/selectors'


const TABLE_BORDER = 1;
const TABLE_PADDING_TOP = 0.25 * 16;

class LinksList extends React.PureComponent {

  static propTypes = {
    currentSite: PropTypes.object,
    currentQueueStatus: PropTypes.shape({
      status: PropTypes.oneOf(queueStatuses).isRequired,
    }),
    height: PropTypes.number.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isSitemapParsed: PropTypes.bool.isRequired,
    links: PropTypes.instanceOf(List).isRequired,
    loadQueueLinks: PropTypes.func.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        queue: PropTypes.string,
        site: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    sort: PropTypes.func.isRequired,
    sorting: PropTypes.instanceOf(Map).isRequired,
    total: PropTypes.number.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    width: PropTypes.number.isRequired,
  }

  /**
   *
   * @param {object} prevProps - previous props
   * @param {object} nextProps - next props
   * @returns {boolean} shouldReloadLinksList
   */
  static shouldReloadLinksList(prevProps, nextProps) {
    const { isSitemapParsed, match: { params: { queueId: prevQueueId } } } = prevProps;
    const { isSitemapParsed: isSitemapBecomeParsed, match: { params: { queueId } } } = nextProps;

    const isNewQueue = (prevQueueId !== queueId);
    const isWarming = (isSitemapParsed !== isSitemapBecomeParsed && isSitemapBecomeParsed);

    return Boolean(isWarming || (isNewQueue && isSitemapBecomeParsed));
  }

  componentDidMount() {
    if (this.props.isSitemapParsed) {
      this.loadData()
    }
  }

  shouldComponentUpdate(nextProps) {
    const { isSitemapParsed, links, match: { params: { queueId } } } = nextProps;
    const { isSitemapParsed: prevIsSitemapParsed, links: prevLinks, match: { params: { queueId: prevQueueId } } } = this.props;
    const isLinksListUpdated = prevLinks !== links;
    const isQueueChanged = prevQueueId !== queueId;

    return prevIsSitemapParsed !== isSitemapParsed || isLinksListUpdated || isQueueChanged;
  }

  componentDidUpdate(prevProps) {
    const { match: { params: { queueId } } } = this.props;
    const shouldReloadLinksList = LinksList.shouldReloadLinksList(prevProps, this.props);

    if (shouldReloadLinksList) {
      this.props.unsubscribe();
      this.loadData(queueId);
    }
  }

  componentWillUnmount() {
    this.props.unsubscribe()
  }

  loadData(nextQueueId) {
    const { loadQueueLinks, match: { params: { queueId } } } = this.props;
    const queue = nextQueueId || queueId;

    if (queue) {
      loadQueueLinks(queue)
    }
  }

  rowGetter = ({ index }) => {
    return this.props.links.get(index)
  }

  renderSmall = ({ rowData, dataKey }) => {
    return <small>{ rowData.get(dataKey) }</small>
  }

  renderLink = ({ rowData, dataKey }) => {
    const link = `${this.props.currentSite.url}/${rowData.get(dataKey)}`
      .replace(/\/{2,}/g, '/')
      .replace(':/', '://')

    return (
      <span>
        <a href={ link } className="mr-2" target="_blank" rel="noopener noreferrer">
          <i className="zmdi zmdi-open-in-new"/>
        </a>
        { rowData.get(dataKey) }
      </span>
    )
  }

  renderStatusCode = ({ rowData }) => {
    const statusCode = rowData.statusCode
    const className = classNames({
      'text-success': statusCode < 300,
      'text-warning': statusCode >= 300 && statusCode < 400,
      'text-danger': statusCode >= 400,
    })
    return <span className={ className }>{ statusCode }</span>
  }

  renderElapsedTime = ({ rowData }) => {
    return rowData.elapsedTime ? (rowData.elapsedTime / 1000) + ' sec' : ''
  }

  noRowsRenderer = () => {
    // eslint-disable-next-line no-invalid-this
    if (this.props.isLoading) {
      return (<div className="links-table-no-rows">
        <i className="zmdi zmdi-spinner zmdi-hc-spin"/>
      </div>)
    }
  }

  render() {
    const { width, height, links, sort, sorting, currentSite } = this.props;

    return (
      <Table
        width={ width }
        height={ height - TABLE_PADDING_TOP - TABLE_BORDER * 2 }
        rowCount={ links.size }
        rowGetter={ this.rowGetter }
        sort={ sort }
        sortBy={ sorting.get('sortBy') }
        sortDirection={ sorting.get('sortDirection') }
        noRowsRenderer={ this.noRowsRenderer }
        headerHeight={ 36 }
        rowHeight={ 36 }
        className="links-table pt-1"
        gridClassName="links-grid-body"
        rowClassName="links-table-row"
        headerClassName="links-table-header"
        overscanRowCount={ 10 }>
        <Column
          dataKey="index"
          label="#"
          width={ 50 }
          cellRenderer={ this.renderSmall }
        />
        <Column
          dataKey="url"
          label="URL"
          width={ 200 }
          disableSort
          flexGrow={ 1 }
          cellRenderer={ this.renderLink }
        />
        <Column
          dataKey="statusCode"
          label="Status"
          width={ 120 }
          cellRenderer={ this.renderStatusCode }
        />
        <Column
          dataKey="elapsedTime"
          label="Time"
          width={ 120 }
          cellRenderer={ this.renderElapsedTime }
        />
        { currentSite.mode === 'crawler' && <Column dataKey="depth" label="Depth" width={60} /> }
      </Table>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  isSitemapParsed,
  isLoading: selectors.isLoadingLinks,
  links: selectors.getSortedVisibleItems,
  sorting: selectors.getQueueSorting,
  total: selectors.getVisibleItemsCount,
})

export default compose(
  withRouter,
  withCurrentSite,
  connect(mapStateToProps, { ...actions }),
)(LinksList)



// WEBPACK FOOTER //
// ./src/components/links/links-list.js